import { STSlideSizeType } from '@tencent/slide-sdk-pb-model';
import { DataType } from '../data';
import { MutationSource } from '../constants/mutation-constants';
import { PresentationDataModel } from '../model/presentation-data-model';
import { MutationManager } from '../manager/mutation-manager';
import { RandomIDGenerator } from '../utils/id-generator';
import { TransactionManager } from '../manager/transaction-manager';
import { CommandManager, CommandSource } from '../manager/command-manager';
import { ObserverManager } from '../manager/observer-manager';
import { createTransactionProvider } from '../manager/event-manager/transaction-events';
import { SearchManager } from '../manager/search-manager';
import { ColorManager } from '../manager/color-manager';
import { ContentStatusEvents, ContentStatusManager } from '../manager/content-status-manager';
import { EventManager } from '../manager/event-manager';
import { CommandEventTypes, PresentationEventTypes } from '../manager/event-manager/event-types';
import { ResourceMapManager } from '../manager/resource-map-manager';
import { SlideFile } from './slide-file';
import { CommandData } from './command-data';
import type { Mutation } from '../mutation/base/base-mutation';
import type { IOTTransform } from '@tencent/slide-sdk-ot-transform';
import type { ISlideFileData } from './slide-file';
import type { ICollaborationManager } from '../manager/collaboration-manager';
import type { IEventManager } from '../manager/event-manager';
import type { ISlideChunksGenerator } from './slide-chunks-generator';
import type { ISearchManager } from '../manager/search-manager';
import type { IContentStatusManager } from '../manager/content-status-manager';
import type { ICommandManager } from '../manager/command-manager';
import type { IMutationManager } from '../manager/mutation-manager';
import type { IPresentationDataModel } from '../model/presentation-data-model';
import type { ICommandData } from '@tencent/slide-sdk-pb-model';
import type { ITransactionProvider } from '../manager/event-manager/transaction-events';
import type { IPresentationData } from '../data';
import type { IColorManager } from '../manager/color-manager';

const SLIDE_DATA_VERSION = '3.0';

/**
 * @deprecated
 */
export interface ISlideApp {
    presentation: IPresentationDataModel;
    transaction: ITransactionProvider;
    getMutationManager(): IMutationManager;
    getCommandManager(): ICommandManager;
    getSearchManager(): ISearchManager;
    getContentStatusManager(): IContentStatusManager;
    getColorManager(): IColorManager;
    getCollaborationManager(): ICollaborationManager | undefined;
    setCollaborationManager(manager: ICollaborationManager): void;
    getEventManager(): IEventManager;
    applyCommandData(command: ICommandData, mutationSource: MutationSource): void;
    getSerializedPresentationData(): ISlideFileData;
    dispose(): void;
}

/**
 * @deprecated
 * 由上层应用负责管理 ISlideApp 的生命周期，此 SlideApp不再维护
 */
export class SlideApp implements ISlideApp {
    static getDefaultPresentationData(): IPresentationData {
        return {
            id: RandomIDGenerator.next(),
            type: DataType.Presentation,
            version: SLIDE_DATA_VERSION,
            slideList: [],
            layoutMap: {},
            noteSlideMap: {},
            notesMasterMap: {},
            masterList: [],
            themeList: [],
            slideGuideList: [],
            sectionMap: {},
            defaultTextStyle: {},
            resourceMap: {
                images: {},
                videos: {},
                audios: {},
                fonts: {},
            },
            sldSz: {
                cx: 12192000,
                cy: 6858000,
                type: STSlideSizeType.screen16x9,
            },
            notesSz: {
                cx: 6858000,
                cy: 9144000,
            },
            showSpecialPlsOnTitleSld: false,
        };
    }

    public static fromDefaultPresentationData(): ISlideApp {
        const app = new SlideApp().init(SlideApp.getDefaultPresentationData());
        app.managers.contentStatus.emitter.emit(ContentStatusEvents.LoadAllChunksSuccess);
        return app;
    }

    public static fromPresentationData(presentationData: IPresentationData): ISlideApp {
        const app = new SlideApp().init(presentationData);
        app.managers.contentStatus.emitter.emit(ContentStatusEvents.LoadAllChunksSuccess);
        return app;
    }

    public static fromCommandData(command: ICommandData): ISlideApp {
        const app = new SlideApp().init(SlideApp.getDefaultPresentationData());
        app.applyCommandData(command, MutationSource.Keyframe);

        app.managers.contentStatus.emitter.emit(ContentStatusEvents.LoadAllChunksSuccess);

        return app;
    }

    public static fromChunksGenerator(headCommand: ICommandData, chunksGenerator: ISlideChunksGenerator): ISlideApp {
        const app = new SlideApp().init(SlideApp.getDefaultPresentationData());
        const mutationManager = app.getMutationManager();
        const MAX_BUFFERED_TIME = 16; // ms
        const MAX_BUFFERED_CHUNK = Infinity;
        let bufferedChunks: { commands: ICommandData[] }[] = [{ commands: [headCommand] }];
        let lastCommitTime = 0;

        const commitBufferedChunks = () => {
            if (bufferedChunks.length > 0) {
                app.transaction(() => {
                    for (const chunk of bufferedChunks) {
                        for (const command of chunk.commands ?? []) {
                            mutationManager.enqueue(CommandData.from(command).getMutations(), MutationSource.Keyframe);
                        }
                    }
                    mutationManager.apply();
                });
                bufferedChunks = [];
            }
        };

        commitBufferedChunks();

        const loadChunks = async () => {
            const MAX_CHUNKS = chunksGenerator.total ?? 65536;
            let i = 0;

            lastCommitTime = performance.now();
            while (i < MAX_CHUNKS) {
                const { done, chunk } = await chunksGenerator.next();
                if (chunk) {
                    bufferedChunks.push(chunk);
                }

                if (
                    performance.now() - lastCommitTime >= MAX_BUFFERED_TIME ||
                    bufferedChunks.length >= MAX_BUFFERED_CHUNK ||
                    done
                ) {
                    commitBufferedChunks();
                    await new Promise((resolve) => setTimeout(resolve));
                    lastCommitTime = performance.now();
                }

                if (i === 0) {
                    app.managers.contentStatus.emitter.emit(ContentStatusEvents.LoadHeadSuccess);
                }

                if (done) {
                    break;
                }

                i += 1;
                if (i >= MAX_CHUNKS) {
                    throw new Error('Exceed max chunks limit.');
                }
            }
        };

        loadChunks()
            .then(() => {
                app.managers.contentStatus.emitter.emit(ContentStatusEvents.LoadAllChunksSuccess);
            })
            .catch((e) => {
                app.managers.contentStatus.emitter.emit(ContentStatusEvents.LoadError, e);
            });

        return app;
    }

    private managers: {
        mutation: MutationManager;
        transaction: TransactionManager;
        command: CommandManager;
        observer: ObserverManager;
        search: SearchManager;
        contentStatus: ContentStatusManager;
        color: ColorManager;
        eventManager: EventManager;
        resourceMap: ResourceMapManager;
        collaboration?: ICollaborationManager;
        transform?: IOTTransform;
    };

    private root: PresentationDataModel;

    private constructor() {
        // Disable Default Constructor
    }

    /**
     * 开启 Mutation 事务，合并多个 MutationRequest，支持同步嵌套事务。
     * 每层事务结束时，会集中应用暂存的 Mutation，最外层事务结束时，会触发变更通知。
     *
     * NOTE: 暂时不支持异步事务
     */
    public transaction: ITransactionProvider = () => {
        throw new Error('Transaction is not available. (Please call `SlideApp.init` first.)');
    };

    public get presentation(): PresentationDataModel {
        if (!this.root) {
            const app = this.init(SlideApp.getDefaultPresentationData());
            return app.presentation;
        }

        return this.root;
    }

    public getMutationManager() {
        return this.managers.mutation;
    }

    public getCommandManager() {
        return this.managers.command;
    }

    public getSearchManager() {
        return this.managers.search;
    }

    public getContentStatusManager() {
        return this.managers.contentStatus;
    }

    public getColorManager() {
        return this.managers.color;
    }

    public getEventManager() {
        return this.managers.eventManager;
    }

    public getCollaborationManager(): ICollaborationManager | undefined {
        return this.managers.collaboration;
    }

    public setCollaborationManager(manager: ICollaborationManager) {
        this.managers.collaboration = manager;
    }

    public getTransformManager(): IOTTransform | undefined {
        return this.managers.transform;
    }

    public setTransformManager(transform: IOTTransform) {
        this.managers.transform = transform;
    }

    public applyCommandData(command: ICommandData, mutationSource: MutationSource): void {
        this.transaction(() => {
            this.getMutationManager().enqueue(CommandData.from(command).getMutations(), mutationSource).apply();
        });
    }

    public applyMutations(mutations: Mutation[], mutationSource: MutationSource): Mutation[] {
        return this.managers.mutation.enqueue(mutations, mutationSource).apply({
            transaction: true,
            inversion: true,
        });
    }

    public dispose() {
        this.root.dispose();
        this.getEventManager().dispose();
    }

    public getSerializedPresentationData() {
        return SlideFile.fromPresentationData(this.presentation.store.data).getData();
    }

    private init(data: IPresentationData = SlideApp.getDefaultPresentationData()): this {
        const eventManager = new EventManager();
        this.root = new PresentationDataModel(data, eventManager);
        this.root.attach();

        this.transaction = createTransactionProvider(this.root.events);

        this.managers = {
            eventManager,
            mutation: new MutationManager(this.root.events).registerPresentation(this.root),
            transaction: new TransactionManager(this.root.events),
            contentStatus: new ContentStatusManager(),
            search: new SearchManager(this.root, this.root.events),

            // 等待数据加载完毕，再进行初始化，避免执行额外的工作
            command: new CommandManager(this.root.events, this),
            observer: new ObserverManager(this.root.events),
            color: new ColorManager(),

            resourceMap: new ResourceMapManager(this.root.events),
        };

        this.root.events.on(PresentationEventTypes.Edit, () => {
            this.managers.contentStatus.emitter.emit(ContentStatusEvents.EditorOperation);
        });
        this.root.events.on(CommandEventTypes.UndoRedoChange, ({ source }) => {
            if (source === CommandSource.Redo) {
                this.managers.contentStatus.emitter.emit(ContentStatusEvents.EditorRedo);
            } else if (source === CommandSource.Undo) {
                this.managers.contentStatus.emitter.emit(ContentStatusEvents.EditorUndo);
            }
        });

        return this;
    }
}
